import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Box, Button, Stack, Typography } from '@mui/material';
import { GraphicEqTwoTone, Print as PrintIcon } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';

// Importe dos relátórios de Gráficos
import GraphHectareHa from '../components/reports/Graph_hectarehora';
import GraphRelevo from '../components/reports/Graph_relevo';
import GraphSementesha from '../components/reports/Graph_sementesha';
import GraphSementesmetro from '../components/reports/Graph_sementesmetro';
import GraphVelocidade from '../components/reports/Graph_velocidade';
import GraphPressao from '../components/reports/Graph_pressao';
import GraphLitrosha from '../components/reports/Graph_litrosha';
import GraphLitrosMin from '../components/reports/Graph_litrosmin';
import GraphTemperatura from '../components/reports/Graph_temperatura';
import MapPrintHeader, { MAP_PRINT_PAGE_STYLE } from '../components/specific/MapPrintHeader';

const ReportsGraphs = ({ data, printTrabalhosInfo }) => {
  const [selectedReport, setSelectedReport] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);

  const hasPulvData = useMemo(() => {
    if (!data) return false;
    if (Array.isArray(data.works) && data.works.length > 0) {
      return data.works.some((work) => Array.isArray(work?.[1]?.histPulv) && work[1].histPulv.length > 0);
    }
    return Array.isArray(data[1]?.histPulv) && data[1].histPulv.length > 0;
  }, [data]);

  const hasPlantioData = useMemo(() => {
    if (!data) return false;
    if (Array.isArray(data.works) && data.works.length > 0) {
      return data.works.some((work) => Array.isArray(work?.[2]?.histPlantio) && work[2].histPlantio.length > 0);
    }
    return Array.isArray(data[2]?.histPlantio) && data[2].histPlantio.length > 0;
  }, [data]);

  const allGraphsList = [
    { id: 'graph_sementesha', label: 'Gráfico Sementes/ha', type: 'Graph', requiresPlantio: true },
    { id: 'graph_sementesmetro', label: 'Gráfico Sementes/metro', type: 'Graph', requiresPlantio: true },
    { id: 'graph_hectarehora', label: 'Gráfico Hectare/hora', type: 'Graph' },
    { id: 'graph_velocidade', label: 'Gráfico Velocidade', type: 'Graph' },
    { id: 'graph_relevo', label: 'Gráfico Relevo', type: 'Graph' },
    { id: 'graph_pressao', label: 'Gráfico Pressão', type: 'Graph', requiresPulv: true },
    { id: 'graph_temperatura', label: 'Gráfico Temperatura', type: 'Graph', requiresPulv: true },
    { id: 'graph_litrosha', label: 'Gráfico Litros/ha', type: 'Graph', requiresPulv: true },
    { id: 'graph_litrosmin', label: 'Gráfico Litros/min', type: 'Graph', requiresPulv: true },
  ];

  const graphsList = useMemo(() => {
    return allGraphsList.filter(graph => {
      if (graph.requiresPulv) {
        return hasPulvData;
      }
      if (graph.requiresPlantio) {
        return hasPlantioData;
      }
      return true;
    });
  }, [hasPulvData, hasPlantioData]);

  const selectedGraphLabel = useMemo(
    () => graphsList.find((graph) => graph.id === selectedReport)?.label || '',
    [graphsList, selectedReport]
  );

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: () => selectedGraphLabel || 'Gráfico',
    pageStyle: MAP_PRINT_PAGE_STYLE,
    onBeforePrint: async () => {
      setIsPrinting(true);
      await new Promise((resolve) => setTimeout(resolve, 150));
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  useEffect(() => {
    const isSelectedGraphAvailable = graphsList.some(graph => graph.id === selectedReport);
    if (selectedReport && !isSelectedGraphAvailable) {
      setSelectedReport('');
    }
  }, [graphsList, selectedReport]);

  const handleChange = (event) => {
    setSelectedReport(event.target.value);
  };

  const renderSelectedGraph = () => {
    switch(selectedReport) {
      case 'graph_sementesha':
        return <GraphSementesha data={data} isPrinting={isPrinting} />;
      case 'graph_sementesmetro':
        return <GraphSementesmetro data={data} isPrinting={isPrinting} />;
      case 'graph_velocidade':
        return <GraphVelocidade data={data} isPrinting={isPrinting} />;
      case 'graph_hectarehora':
        return <GraphHectareHa data={data} isPrinting={isPrinting} />;
      case 'graph_pressao':
        return <GraphPressao data={data} isPrinting={isPrinting} />;
      case 'graph_litrosha':
        return <GraphLitrosha data={data} isPrinting={isPrinting} />;
      case 'graph_litrosmin':
        return <GraphLitrosMin data={data} isPrinting={isPrinting} />;
      case 'graph_temperatura':
        return <GraphTemperatura data={data} isPrinting={isPrinting} />;
      case 'graph_relevo':
        return <GraphRelevo data={data} isPrinting={isPrinting} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Stack
        className="no-print"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2, width: '100%', maxWidth: '100%', minWidth: 0 }}
      >
        <FormControl fullWidth variant="outlined" sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
          <InputLabel id="graph-select-label">
            Selecione um Gráfico
          </InputLabel>
          <Select
            labelId="graph-select-label"
            id="graph-select"
            value={selectedReport}
            onChange={handleChange}
            label="Selecione um Gráfico"
            startAdornment={
              <GraphicEqTwoTone sx={{ mr: 1, color: 'action.active', flexShrink: 0 }} fontSize="small" />
            }
            MenuProps={{
              PaperProps: {
                sx: { maxWidth: 'calc(100vw - 32px)' },
              },
            }}
            sx={{
              '& .MuiSelect-select': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              },
            }}
          >
            <MenuItem value="">
              <em>Nenhum selecionado</em>
            </MenuItem>
            {graphsList.map(graph => (
              <MenuItem
                key={graph.id}
                value={graph.id}
                sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {graph.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={!selectedReport}
          sx={{ whiteSpace: 'nowrap', minWidth: { xs: '100%', sm: 160 }, flexShrink: 0 }}
        >
          Imprimir gráfico
        </Button>
      </Stack>

      <Box
        ref={printRef}
        sx={{
          marginTop: selectedReport ? '16px' : 0,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          color: isPrinting ? '#000' : 'inherit',
          '& h3': isPrinting ? { color: '#000' } : undefined,
        }}
      >
        {selectedReport && isPrinting && Array.isArray(printTrabalhosInfo) && printTrabalhosInfo.length > 0 && (
          printTrabalhosInfo.map((info) => (
            <MapPrintHeader key={info.id ?? info.dataInicial} {...info} />
          ))
        )}
        {selectedReport && selectedGraphLabel && (
          <Typography
            variant="h5"
            component="h2"
            sx={{ mb: 2, fontWeight: 600, color: isPrinting ? '#000' : 'text.primary' }}
          >
            {selectedGraphLabel}
          </Typography>
        )}
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
          {renderSelectedGraph()}
        </Box>
      </Box>
    </Box>
  );
};

export default ReportsGraphs;
